import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {debounceTime, switchMap, takeUntil, tap} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';
import {Sort} from '@angular/material/sort';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {CaseDTO} from '../../../models/CaseDTO';
import {DateUtil} from '../../../../common/util/DateUtil';
import {PagedRequestDTO} from '../../../../common/models/PagedRequestDTO';
import {TableControl} from '../../../../common/models/TableControl';
import {CaseFuneralService} from '../../../services/case-funeral.service';
import {PagedResponseDTO} from '../../../../common/models/PagedResponseDTO';
import {
  ConfirmationDialogComponent
} from '../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {CaseStatus, CaseType} from '../../../types';
import {NameValueDTO} from '../../../../common/models/NameValueDTO';
import {LookupValue} from '../../../types/lookup-value.enum';
import {LookupService} from '../../../../common/services/lookup.service';
import {forkJoin, Subject} from 'rxjs';
import {ClmRequestBuilder} from '../../../services/clm-request-builder';
import {FormControl} from '@angular/forms';
import {PagedFilterDTO, PagedFilterDTONew} from '../../../../common/models/PagedFilterDTO';
import {KeycloakService} from 'keycloak-angular';
import {Common} from '../../../../common/util/Util';
import {SharedService} from '../../../../common/services/shared-service';
import {ReportingService} from '../../../../other/services/reporting.service';
import {DatePipe} from '@angular/common';
import {saveAs} from 'file-saver';
import {CaseFilterCacheService, CaseFilterKeys, CaseFilterType} from '../../../services/case-filter-cache.service';
import {detailsExpand} from '../../../../common';
import {ClmRoutes, ProcessingClassSubTypeDBMapping} from '../../../types/types';
import {ProcessingClassDisplayValue} from '../../../types/processing-class.enum';
import {UserAuthorisationService} from 'src/app/modules/common/services/user-authorisation.service';
import {EntityTable} from '../../../../common/types/entity-table.enum';

// Note: value provided here are what BE accepts for 'fieldMap'
enum SelectDropdownFilterName {
  CASE_STATUS = 'caseStatus',
  CASE_MANAGER = 'caseManager',
  PROCESSING_CLASS_BY_PROCESSING_ClASS_ID = 'processingClassByProcessingClassId',
  ProcessingSubClassByProcessingSubClassId = 'processingSubClassByProcessingSubClassId'
}

enum TableColumns {
  Icons = 'icons',
  CaseId = 'caseId',
  ProcessingClass = 'processingClass',
  Name = 'name',
  StartDate = 'startDate',
  InsuredPerson = 'insuredPerson',
  CaseStatus = 'caseStatus',
  CaseManager = 'caseManager',
  ElapsedDays = 'elapsedDays'
}

@Component({
  selector: 'app-cases',
  templateUrl: './cases.component.html',
  styleUrls: [
    '../../../../../../styles/global/_management-styles.scss'
  ],
  animations: [detailsExpand]
})
export class CasesComponent implements OnInit, OnDestroy {
  readonly ALL_STATUSES = 'All Statuses';
  readonly ALL_MANAGERS = 'All Managers';
  readonly ALL_CLASSES = 'All Classes';
  readonly ALL_SUB_CLASSES = 'All Sub Classes';
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly destroyed$ = new Subject();
  readonly dateFormat = DateUtil.dateFormat;
  readonly dateTimeFormat = DateUtil.dateTimeFormat;
  readonly caseType = CaseType;
  readonly caseStatus = CaseStatus;
  readonly clmRoutes = ClmRoutes;
  readonly lookupValue = LookupValue;
  readonly tableColumns = TableColumns;
  readonly selectDropdownFilterName = SelectDropdownFilterName;
  readonly caseByNameTextChanged$ = new Subject<string>();

  length = 100;
  pageSize = 10;
  pageNumber = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];
  casedTableDataSource: CaseDTO[];
  caseTableControl: TableControl;
  casedTableDisplayedColumns: string[] = Object.values(TableColumns);
  caseExpandedRow: CaseDTO | null;


  selectedProcessingClass = this.ALL_CLASSES;
  selectedCaseStatus = this.ALL_STATUSES;
  selectedCaseManager = this.ALL_MANAGERS;
  selectedProcessingSubClass: string;
  caseSearchNameFormControl: FormControl;
  caseStatusFormControl: FormControl;
  caseManagerFormControl: FormControl;
  processingClassFormControl: FormControl;

  caseStatusLookupValues: NameValueDTO[];
  caseManagerLookupValues: NameValueDTO[];
  processingClassLookupValues: NameValueDTO[] = [
    {value: null, name: this.ALL_CLASSES},
    {value: 3, name: 'Funeral'},
    {value: 1, name: 'Medical'}
  ];
  processingSubClassesOptions: NameValueDTO[] = [];
  private medicalProcessingSubClassesOptions: NameValueDTO[] = [];
  private funeralProcessingSubClassesOptions: NameValueDTO[] = [];

  @ViewChild('casePaginator', {static: false}) casePaginator: MatPaginator;
  private loggedInUserName: string;

  constructor(public dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private lookupService: LookupService,
    private caseFilterCacheService: CaseFilterCacheService,
    private snackBar: MatSnackBar,
    private sharedService: SharedService,
    private caseFuneralService: CaseFuneralService,
    private keycloakService: KeycloakService,
    private reportingService: ReportingService,
    public datePipe: DatePipe,
    private userAuthorisationService: UserAuthorisationService) {
    this.caseTableControl = new TableControl(
      this.length,
      this.pageSize,
      this.pageNumber,
      this.pageSizeOptions,
      'caseId',
      'desc',
      'name',
      '');
  }

  get isAuthorised(): boolean {
    return this.userAuthorisationService.hasRoleClaimsAssessor();
  }

  ngOnInit(): void {
    this.initFormControls();
    this.initSubscriptions();
    this.initLookUpValues();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initFormControls() {
    this.caseSearchNameFormControl = new FormControl();
    this.processingClassFormControl = new FormControl(this.ALL_CLASSES);
    this.caseStatusFormControl = new FormControl(this.ALL_STATUSES);
    this.caseManagerFormControl = new FormControl(this.ALL_MANAGERS);
  }

  initSubscriptions() {
    this.caseFilterCacheService.getFilterValue()
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((cachedValues: CaseFilterType) => {
        if (cachedValues && Object.keys(cachedValues)?.length > 0) {
          this.setFilterValue(cachedValues);
        }
        this.getListOfCases(this.caseTableControl);
      });

    this.route
      .queryParams
      .subscribe((params: any) => {
        if (params.manager && params.status) {
          // Imp!! GET cases by manager and status defined in query params.
          const manager = params.manager;
          const status = params.status;

          this.caseFuneralService.getListOfFuneralCases(this.getRecordsByManagerAndStatusRequestDTO(manager, status))
            .pipe(
              takeUntil(this.destroyed$)
            )
            .subscribe(
              (response) => {
                if (response.totalRecords > 0) {
                  this.selectedCaseManager = manager;
                  this.selectedCaseStatus = status;
                }
                this.caseManagerFormControl.setValue(manager);
                this.caseStatusFormControl.setValue(status);

                this.caseFilterCacheService.saveMultipleFilterValue({
                  [CaseFilterKeys.CaseManager]: manager,
                  [CaseFilterKeys.CaseStatus]: status,
                });
              }
            );
        }
      });

    // Note: Don't add "distinctUntilChanged" since it is also called with
    // - same input
    // - page change even if input is not changed:

    this.caseByNameTextChanged$
      .pipe(
        takeUntil(this.destroyed$),
        debounceTime(this.debounceTime)
      )
      .subscribe((searchString) => {
          this.caseTableControl.filterValue = searchString;
          this.caseFilterCacheService.saveFilterValue(CaseFilterKeys.SearchCase, searchString);
        },
        error => console.log(error));

    // ----------------------------------------
    this.keycloakService
      .loadUserProfile()
      .then(profile => {
        this.loggedInUserName = `${profile.firstName} ${profile.lastName}`;
      });
  }

  initLookUpValues() {
    // - Default: show "open" cases.
    // - Default: showcases assigned to "loggedInUserName" if logged-in user exists in the managers list.

    forkJoin(
      this.lookupService.getListOfLookupValues(this.lookupValue.CaseStatus),
      this.lookupService.getListOfLookupValues(this.lookupValue.ProcessingSubClassMedical),
      this.lookupService.getListOfLookupValues(this.lookupValue.ProcessingSubClassFuneral)
    )
      .pipe(
        tap(responses => {
          responses[0].unshift({value: null, name: this.ALL_STATUSES});
          this.caseStatusLookupValues = responses[0];
          this.medicalProcessingSubClassesOptions = responses[1];
          this.funeralProcessingSubClassesOptions = responses[2];
          this.updateProcessingSubClassOptions();
        }),
        switchMap(_ => {
          return this.lookupService.getListOfLookupValues(this.lookupValue.CaseManager);
        })
      )
      .subscribe(response => {
        response.unshift({value: null, name: this.ALL_MANAGERS});
        this.caseManagerLookupValues = response;
      });
  }

  onInputChangedSearchCase($event) {
    this.caseByNameTextChanged$.next($event.target.value);
  }

  setFilterValue(cachedValues: CaseFilterType): void {
    const {
      searchCase,
      processingClass,
      processingSubClass,
      caseStatus,
      caseManager,
      pageNumber,
      pageSize,
      sortField,
      sortDirection,
    } = cachedValues;

    this.caseTableControl.filterValue = searchCase ?? '';
    this.caseSearchNameFormControl.setValue(searchCase, {emitEvent: true});

    // Setting to "ALL_<TYPE> will prevent to be added in fieldMap see list of getListOfCases method.
    this.selectedProcessingClass = processingClass ?? this.ALL_CLASSES;
    this.selectedCaseManager = caseManager ?? this.ALL_MANAGERS;
    this.selectedCaseStatus = caseStatus ?? this.ALL_STATUSES;
    this.selectedProcessingSubClass = processingSubClass ?? '';

    this.processingClassFormControl.setValue(this.selectedProcessingClass, {emitEvent: true});
    this.caseManagerFormControl.setValue(this.selectedCaseManager, {emitEvent: true});
    this.caseStatusFormControl.setValue(this.selectedCaseStatus, {emitEvent: true});

    // Update Table control cached values
    this.caseTableControl.pageSize = pageSize || this.caseTableControl.pageSize;
    this.caseTableControl.pageNumber = pageNumber || this.caseTableControl.pageNumber;
    this.caseTableControl.sortField = sortField || this.caseTableControl.sortField;
    this.caseTableControl.sortDirection = sortDirection || this.caseTableControl.sortDirection;
  }

  getListOfCases(tableControl: TableControl): void {

    const pagedRequestDTO = ClmRequestBuilder.getListOfCasesPagedRequestDTO(tableControl);
    pagedRequestDTO.fieldMap = {};

    if (this.selectedProcessingClass !== this.ALL_CLASSES) {
      const classPagedFilterDTO = new PagedFilterDTO();
      classPagedFilterDTO.name = 'processingClassByProcessingClassId.className';
      classPagedFilterDTO.value = this.selectedProcessingClass;
      pagedRequestDTO.fieldMap[SelectDropdownFilterName.PROCESSING_CLASS_BY_PROCESSING_ClASS_ID] = classPagedFilterDTO;
    }

    // Note: In Future in case Funeral has subtype than we will update this to pass for funeral types.
    if (this.selectedProcessingClass === CaseType.Medical && this.selectedProcessingSubClass !== this.ALL_SUB_CLASSES) {
      const classPagedFilterDTO = new PagedFilterDTO();
      classPagedFilterDTO.name = 'ProcessingSubClassByProcessingSubClassId.name';
      classPagedFilterDTO.value = this.selectedProcessingSubClass;
      pagedRequestDTO.fieldMap[SelectDropdownFilterName.ProcessingSubClassByProcessingSubClassId] = classPagedFilterDTO;
    }

    if (this.selectedCaseStatus !== this.ALL_STATUSES) {
      const typePagedFilterDTO = new PagedFilterDTO();
      typePagedFilterDTO.name = 'name';
      typePagedFilterDTO.value = this.selectedCaseStatus;
      pagedRequestDTO.fieldMap[SelectDropdownFilterName.CASE_STATUS] = typePagedFilterDTO;
    }

    if (this.selectedCaseManager !== this.ALL_MANAGERS) {
      const statusPagedFilterDTO = new PagedFilterDTO();
      statusPagedFilterDTO.name = 'name';
      statusPagedFilterDTO.value = this.selectedCaseManager;
      pagedRequestDTO.fieldMap[SelectDropdownFilterName.CASE_MANAGER] = statusPagedFilterDTO;
    }

    if (this.caseTableControl != null && this.caseTableControl.filterValue?.trim() !== '') {
      const value = this.caseTableControl.filterValue?.trim();

      const personIdNumberPagedFilterDTO = new PagedFilterDTONew();
      personIdNumberPagedFilterDTO.name = 'insuredPersonByInsuredPersonId.nationalIdNumber';
      personIdNumberPagedFilterDTO.value = value;
      personIdNumberPagedFilterDTO.logicGate = 'AND';
      personIdNumberPagedFilterDTO.bracket = '(';
      pagedRequestDTO.fieldMap[`insuredPersonByInsuredPersonId_1`] = personIdNumberPagedFilterDTO;

      const personLastNamePagedFilterDTO = new PagedFilterDTONew();
      personLastNamePagedFilterDTO.name = 'insuredPersonByInsuredPersonId.lastName';
      personLastNamePagedFilterDTO.value = value;
      personLastNamePagedFilterDTO.logicGate = 'OR';
      personLastNamePagedFilterDTO.bracket = '';
      pagedRequestDTO.fieldMap[`insuredPersonByInsuredPersonId_2`] = personLastNamePagedFilterDTO;

      const personMemberNumberNamePagedFilterDTO = new PagedFilterDTONew();
      personMemberNumberNamePagedFilterDTO.name = 'insuredPersonByInsuredPersonId.cardNumber';
      personMemberNumberNamePagedFilterDTO.value = value;
      personMemberNumberNamePagedFilterDTO.logicGate = 'OR';
      personMemberNumberNamePagedFilterDTO.bracket = '';
      pagedRequestDTO.fieldMap[`insuredPersonByInsuredPersonId_3`] = personMemberNumberNamePagedFilterDTO;

      if (!isNaN(Number(value))) {
        const caseIdPagedFilterDTO = new PagedFilterDTONew();
        caseIdPagedFilterDTO.name = 'caseId';
        caseIdPagedFilterDTO.value = value;
        caseIdPagedFilterDTO.operator = 'in';
        caseIdPagedFilterDTO.logicGate = 'OR';
        caseIdPagedFilterDTO.bracket = '';
        pagedRequestDTO.fieldMap[`caseId`] = caseIdPagedFilterDTO;
      }

      const caseNamePagedFilterDTO = new PagedFilterDTONew();
      caseNamePagedFilterDTO.name = 'name';
      caseNamePagedFilterDTO.value = value;
      caseNamePagedFilterDTO.logicGate = 'OR';
      caseNamePagedFilterDTO.bracket = ')';
      pagedRequestDTO.fieldMap[`name`] = caseNamePagedFilterDTO;
    }

    this.caseFuneralService.getListOfFuneralCases(pagedRequestDTO)
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe(
        (response: PagedResponseDTO<CaseDTO>) => {
          this.casedTableDataSource = response?.resultList || [];
          this.caseTableControl.length = response?.totalRecords || 0;
          this.setPaginatorProperties(this.caseTableControl.pageSize, this.caseTableControl.pageNumber); // pageIndex ~~ pageNumber
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  setPaginatorProperties(pageSize: number, pageIndex: number): void {
    if (this.casePaginator) {
      this.casePaginator.pageIndex = pageIndex;
      this.casePaginator.pageSize = pageSize;
    }
  }

  sortData($event: Sort) {
    if (!$event.direction) {
      return;
    }
    if (this.casePaginator) {
      this.casePaginator.firstPage();
    }
    this.caseTableControl.pageNumber = 0;

    if ($event.active === 'insuredPerson') {
      this.caseTableControl.sortField = 'insuredPersonByInsuredPersonId.lastName';
    }
    else if ($event.active === 'processingClass') {
      this.caseTableControl.sortField = 'processingClassByProcessingClassId.className';
    }
    else {
      this.caseTableControl.sortField = $event.active;
    }

    this.caseTableControl.sortDirection = $event.direction;

    this.caseFilterCacheService.saveMultipleFilterValue({
      [CaseFilterKeys.SortField]: this.caseTableControl.sortField,
      [CaseFilterKeys.SortDirection]: this.caseTableControl.sortDirection,
    });
  }

  pageChanged($event: PageEvent) {
    this.caseTableControl.pageNumber = $event.pageIndex;
    this.caseTableControl.pageSize = $event.pageSize;
    this.caseFilterCacheService.saveMultipleFilterValue({
      [CaseFilterKeys.PageNumber]: this.caseTableControl.pageNumber,
      [CaseFilterKeys.PageSize]: this.caseTableControl.pageSize,
    });
  }

  deleteCaseConfirmation(event: MouseEvent, id: number) {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: 'Are you sure you wish to delete this entity?'
    });

    dialogRef.afterClosed().subscribe((isConfirmed) => {
      if (isConfirmed) {
        this.caseFuneralService.deleteFuneralCase(id).subscribe(
          () => {
            this.sharedService.openSnackBar(`Case Funeral ${id} deleted`, 'Dismiss');
            this.getListOfCases(this.caseTableControl);
          },
          (errorResponse) => {
            const error = errorResponse.error;
            this.sharedService.openErrorDialog(error.businessMessage);
          }
        );
      }
    });
  }

  editEntry(event: MouseEvent, row: CaseDTO) {
    event.preventDefault();
    event.stopPropagation();

    if (row.processingClass.name === CaseType.Medical) {
      const MedicalProcessingClassSubType: NameValueDTO = row.processingSubClass;

      if (MedicalProcessingClassSubType?.value === ProcessingClassSubTypeDBMapping.Pharmacy) {
        this.router.navigate([`${this.clmRoutes.EditPharmacyCase}/${row.caseId}`]);
      }
      else {
        this.router.navigate([`${this.clmRoutes.EditHospitalCase}/${row.caseId}`]);
      }
    }
    else if (row.processingClass.name === CaseType.Funeral) {
      this.router.navigate([`${this.clmRoutes.EditFuneralCase}/${row.caseId}`]);
    }
  }

  generateCaseReport(event: MouseEvent, id: number, processingClass: string) {
    event.preventDefault();
    event.stopPropagation();
    let reportingUrl = '';
    const parameter = '';
    if (processingClass === this.caseType.Medical) {
      reportingUrl = '/Reports/Case_Management_Report';
    }
    else if (processingClass === this.caseType.Funeral) {
      reportingUrl = '/Reports/Case_Management_Funeral';
    }
    if (reportingUrl !== '') {
      this.reportingService.generateReport(reportingUrl, 'pdf', `Case_Id=${id}`).subscribe(
        (response) => {
          saveAs(response, `case_management_${id}_${this.datePipe.transform(new Date(), 'yyyyMMdd_HHmm')}.pdf`);
        },
        () => {
          this.sharedService.openSnackBar('There was an error generating the report', 'Dismiss');
        },
      );
    }
  }

  clearSearchCasesFilter() {
    if (this.casePaginator) {
      this.casePaginator.pageIndex = this.pageNumber;
      this.casePaginator.pageSize = this.pageSize;
    }

    // Note: Rest of the filters will set to "ALL_<TYPE>"
    // so in case even if value exists as string they won't
    // be added in fieldMap therefore treated as default filter
    // "ALL_<TYPE>" values

    this.caseFilterCacheService.clearFilterValue(CaseFilterKeys.SearchCase);
  }

  onProcessingClassSelectionChanged(name: string) {
    this.selectedProcessingClass = name;
    this.updateProcessingSubClassOptions();
    this.caseFilterCacheService.saveFilterValue(CaseFilterKeys.ProcessingClass, name);
  }

  onCaseStatusSelectionChanged(name: string) {
    this.selectedCaseStatus = name;
    this.caseFilterCacheService.saveFilterValue(CaseFilterKeys.CaseStatus, name);
  }

  onCaseManagerSelectionChanged(name: string) {
    this.selectedCaseManager = name;
    this.caseFilterCacheService.saveFilterValue(CaseFilterKeys.CaseManager, name);
  }


  onProcessingSubClassSelectionChanged(name: string) {
    this.selectedProcessingSubClass = name;
    this.caseFilterCacheService.saveFilterValue(CaseFilterKeys.ProcessingSubClass, name);
  }


  disabledProcessingSubClass(): boolean {
    if (this.selectedProcessingClass === this.ALL_CLASSES) {
      return true;
    }
    return false;
  }

  private updateProcessingSubClassOptions() {
    this.processingSubClassesOptions = [];

    if (this.selectedProcessingClass === ProcessingClassDisplayValue.Medical) {
      const updatedOptions: NameValueDTO[] = [{
        value: null,
        name: this.ALL_SUB_CLASSES
      }, ...this.medicalProcessingSubClassesOptions];
      this.processingSubClassesOptions = updatedOptions;
    }
    else if (this.selectedProcessingClass === ProcessingClassDisplayValue.Funeral) {
      const updatedOptions: NameValueDTO[] = [{
        value: null,
        name: this.ALL_SUB_CLASSES
      }, ...this.funeralProcessingSubClassesOptions];
      this.processingSubClassesOptions = updatedOptions;
    }
  }


  private getRecordsByManagerAndStatusRequestDTO(manager: string, status: CaseStatus): PagedRequestDTO {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblCase;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = 1;

    const statusPagedFilterDTO = new PagedFilterDTO();
    statusPagedFilterDTO.name = 'name';
    statusPagedFilterDTO.value = status;

    const caseManagerPagedFilterDTO = new PagedFilterDTO();
    caseManagerPagedFilterDTO.name = 'name';
    caseManagerPagedFilterDTO.value = manager;

    pagedRequestDTO.fieldMap = {};
    pagedRequestDTO.fieldMap[`caseManager`] = caseManagerPagedFilterDTO;
    pagedRequestDTO.fieldMap[`caseStatus`] = statusPagedFilterDTO;
    return pagedRequestDTO;
  }

}
